<div class="flex h-full w-full flex-col gap-y-5 overflow-y-auto p-4 scrollbar-transparent">
  <!-- Moderation -->
  @switch (moderationStatus()) {
    @case ('cleared') {
      <div
        class="flex flex-col gap-y-1.5 rounded-lg border border-green-600/25 bg-green-50 p-4 dark:bg-green-900/20"
      >
        <p class="m-0 text-sm font-bold text-success">Cleared by SHOUT moderation</p>
        @if (moderationReasons().length > 0) {
          <p class="m-0 text-sm text-secondary">{{ moderationReasons().join(' · ') }}</p>
        }
      </div>
    }
    @case ('in_moderation') {
      <div
        class="flex flex-col gap-y-1.5 rounded-lg border border-amber-600/25 bg-amber-50 p-4 dark:bg-amber-900/20"
      >
        <p class="m-0 text-sm font-bold text-amber-700 dark:text-amber-300">Still in moderation</p>
        <p class="m-0 text-sm text-secondary">
          This post has not been approved yet. You can still request the creator’s permission.
        </p>
      </div>
    }
    @case ('declined') {
      <div
        class="flex flex-col gap-y-1.5 rounded-lg border border-destructive/30 bg-destructive/5 p-4"
      >
        <p class="m-0 text-sm font-bold text-error">Declined by moderation</p>
        <p class="m-0 text-sm text-secondary">This post is not eligible to carry budget.</p>
      </div>
    }
    @default {
      <div class="flex flex-col gap-y-1.5 rounded-lg border border-border bg-muted/50 p-4">
        <p class="m-0 text-sm font-bold text-primary">Moderation status unknown</p>
        <p class="m-0 text-sm text-secondary">
          We could not trace this TikTok post back to a moderated SHOUT video.
        </p>
      </div>
    }
  }

  <!-- ── Step 1 · Spark authorisation ──────────────────────────────────────── -->
  <section class="flex flex-col gap-y-3.5">
    <header class="flex flex-col gap-y-1">
      <p class="m-0 text-xs font-bold tracking-[0.08em] text-brand-studio uppercase">Step 1</p>
      <h2 class="m-0 text-lg font-semibold text-primary">Spark authorisation</h2>
      <p class="m-0 text-sm text-secondary">
        You need the creator’s permission code before this post can carry budget. The post always
        stays on their account.
      </p>
    </header>

    @if (authState() === 'loading') {
      <div aria-busy="true" class="flex flex-col gap-y-2">
        <div class="h-11 animate-pulse rounded-lg bg-gray-200 dark:bg-white/10"></div>
        <div class="h-3 w-2/3 animate-pulse rounded bg-gray-200 dark:bg-white/10"></div>
      </div>
    } @else if (authState() === 'error') {
      <div role="alert" class="flex flex-col items-start gap-y-2.5">
        <p class="m-0 text-sm text-error">{{ actionError() }}</p>
        <button type="button" (click)="retryAuth()" class="secondary-button">Try again</button>
      </div>
    } @else {
      @if (isAuthorised()) {
        <!-- Authorised -->
        <div
          class="flex flex-col gap-y-2.5 rounded-lg border border-green-600/25 bg-green-50 p-4 dark:bg-green-900/20"
        >
          <p class="m-0 text-sm font-bold text-success">Authorised</p>

          @if (auth()?.authCode) {
            <div class="flex flex-row items-center gap-x-2.5">
              <p
                class="m-0 min-w-0 flex-1 truncate rounded-lg border border-border bg-card px-3 py-2 text-sm font-bold text-primary"
              >
                {{ auth()!.authCode }}
              </p>
              <button
                type="button"
                (click)="copyCode()"
                class="shrink-0 cursor-pointer text-sm font-bold text-brand-studio hover:underline"
              >
                Copy
              </button>
            </div>
          }

          @if (expiryLabel()) {
            <p class="m-0 text-sm text-secondary">{{ expiryLabel() }}</p>
          }
        </div>
      } @else if (isRequested()) {
        <!-- Requested, waiting on the creator -->
        <div class="flex flex-col gap-y-3">
          <div
            class="flex flex-row gap-x-3 rounded-lg border border-amber-600/25 bg-amber-50 p-4 dark:bg-amber-900/20"
          >
            <div aria-hidden="true" class="mt-1.5 size-2 shrink-0 rounded-full bg-amber-500"></div>
            <div class="flex flex-col gap-y-1">
              <p class="m-0 text-sm font-bold text-amber-700 dark:text-amber-300">
                Requested · waiting on the creator
              </p>
              <p class="m-0 text-sm text-secondary">
                The creator still needs to accept in TikTok. Check again once they have — most come
                back inside an hour.
              </p>
            </div>
          </div>

          <div class="flex flex-row flex-wrap gap-2">
            <button type="button" (click)="retryAuth()" [disabled]="isBusy()" class="secondary-button">
              Check again
            </button>
            <button type="button" (click)="toggleCodeEntry()" class="secondary-button">
              Creator sent it another way — paste code
            </button>
          </div>
        </div>
      } @else if (isExpired()) {
        <!-- Expired -->
        <div class="flex flex-col gap-y-3">
          <div
            class="flex flex-col gap-y-1 rounded-lg border border-amber-600/25 bg-amber-50 p-4 dark:bg-amber-900/20"
          >
            <p class="m-0 text-sm font-bold text-amber-700 dark:text-amber-300">
              Permission expired
            </p>
            @if (expiryLabel()) {
              <p class="m-0 text-sm text-secondary">{{ expiryLabel() }}</p>
            }
          </div>

          <button
            type="button"
            (click)="requestAuth()"
            [disabled]="isBusy()"
            class="primary-button"
          >
            {{ action() === 'requesting' ? 'Requesting…' : 'Request a renewal from ' + handle() }}
          </button>
        </div>
      } @else if (isRejected()) {
        <div class="flex flex-col gap-y-3">
          <div
            class="flex flex-col gap-y-1 rounded-lg border border-destructive/30 bg-destructive/5 p-4"
          >
            <p class="m-0 text-sm font-bold text-error">Creator declined</p>
            <p class="m-0 text-sm text-secondary">
              They turned down the Spark request. You can ask again.
            </p>
          </div>

          <button
            type="button"
            (click)="requestAuth()"
            [disabled]="isBusy()"
            class="primary-button"
          >
            {{ action() === 'requesting' ? 'Requesting…' : 'Request Spark code from ' + handle() }}
          </button>
        </div>
      } @else if (isUnknown()) {
        <!-- Could not reach TikTok: say so rather than implying either state -->
        <div class="flex flex-col gap-y-3">
          <div class="flex flex-col gap-y-1 rounded-lg border border-border bg-muted/50 p-4">
            <p class="m-0 text-sm font-bold text-primary">Permission status unknown</p>
            <p class="m-0 text-sm text-secondary">
              We could not reach TikTok to check this post. Try again in a moment.
            </p>
          </div>
          <button type="button" (click)="retryAuth()" class="secondary-button">
            Check again
          </button>
        </div>
      } @else {
        <!-- Not requested -->
        <div class="flex flex-col gap-y-3">
          <button
            type="button"
            (click)="requestAuth()"
            [disabled]="isBusy()"
            class="primary-button"
          >
            {{ action() === 'requesting' ? 'Requesting…' : 'Request Spark code from ' + handle() }}
          </button>
          <p class="m-0 text-sm text-secondary">
            We’ll send them a one-tap request with step-by-step instructions. Most come back inside
            an hour.
          </p>
          <button
            type="button"
            (click)="toggleCodeEntry()"
            class="cursor-pointer self-start text-sm font-semibold text-brand-studio hover:underline"
          >
            Already have a code? Paste it instead
          </button>
        </div>
      }

      <!-- Paste-a-code entry -->
      @if (showCodeEntry()) {
        <div class="flex flex-col gap-y-2 rounded-lg border border-border bg-muted/40 p-4">
          <label for="spark-paste-code" class="text-sm/6 font-medium text-primary">
            Spark authorisation code
          </label>
          <input
            id="spark-paste-code"
            type="text"
            class="input-primary"
            [value]="pastedCode()"
            (input)="setPastedCode($any($event.target).value)"
          />
          <p class="m-0 text-xs text-secondary">
            The creator generates this in TikTok → Creator Tools → Ad Settings.
          </p>
          <div class="flex flex-row gap-x-2">
            <button
              type="button"
              (click)="applyCode()"
              [disabled]="isBusy() || !pastedCode().trim()"
              class="primary-button"
            >
              {{ action() === 'applying' ? 'Applying…' : 'Apply code' }}
            </button>
            <button type="button" (click)="toggleCodeEntry()" class="secondary-button">
              Cancel
            </button>
          </div>
        </div>
      }

      @if (actionError() && authState() !== 'error') {
        <p role="alert" class="m-0 text-sm text-error">{{ actionError() }}</p>
      }
    }
  </section>

  <div class="h-px bg-border"></div>

  <!-- ── Step 2 · Put budget behind it ─────────────────────────────────────── -->
  <section
    class="flex flex-col gap-y-3.5 transition-opacity duration-200"
    [class]="isAuthorised() ? 'opacity-100' : 'opacity-60'"
  >
    <header class="flex flex-col gap-y-1">
      <p class="m-0 text-xs font-bold tracking-[0.08em] text-brand-studio uppercase">Step 2</p>
      <h2 class="m-0 text-lg font-semibold text-primary">Put budget behind it</h2>
      @if (step2LockedReason(); as reason) {
        <!-- Colour is never the only signal: the reason is stated in words -->
        <p class="m-0 text-sm font-medium text-amber-700 dark:text-amber-400">{{ reason }}</p>
      }
    </header>

    @if (alreadySparked()) {
      <div class="flex flex-col gap-y-2.5 rounded-xl bg-brand-deep-purple p-5 text-white">
        <p class="m-0 text-base font-bold">Sparked. Turn up the volume.</p>
        <p class="m-0 text-sm text-white/80">
          @if (sparkedAdGroupName(); as groupName) {
            This post is now a Spark ad in <strong class="text-white">{{ groupName }}</strong>. It
            goes into TikTok review immediately.
          } @else {
            This post is already running as a Spark ad.
          }
        </p>
        <button type="button" (click)="goToDashboard()" class="primary-button self-start">
          Track it on the ads dashboard
        </button>
      </div>
    } @else if (hasAdGroups()) {
      <div class="flex flex-col gap-y-2.5">
        <p class="m-0 text-sm font-bold text-primary">Add to an existing ad group</p>

        <fieldset
          class="m-0 flex flex-col gap-y-2 border-0 p-0"
          [disabled]="!isAuthorised() || isBusy()"
        >
          <legend class="sr-only">Choose an ad group</legend>
          @for (adGroup of adGroups(); track adGroup.id) {
            <label
              class="flex cursor-pointer flex-row items-center gap-x-3 rounded-lg p-3.5 transition-colors"
              [class]="adGroupRowClass(adGroup.tiktokAdGroupId ?? '')"
            >
              <input
                type="radio"
                name="spark-ad-group"
                class="size-4 accent-[var(--brand-studio)]"
                [checked]="selectedAdGroupId() === adGroup.tiktokAdGroupId"
                [disabled]="!isAuthorised() || isBusy()"
                (change)="selectAdGroup(adGroup.tiktokAdGroupId ?? '')"
              />
              <span class="flex min-w-0 flex-col gap-y-0.5">
                <span class="truncate text-sm font-bold text-primary">
                  {{ adGroup.adgroupName ?? 'Untitled ad group' }}
                </span>
                <span class="text-xs text-secondary">{{ adGroupMeta(adGroup) }}</span>
              </span>
            </label>
          }
        </fieldset>

        <button
          type="button"
          (click)="createAdGroupForThisPost()"
          class="cursor-pointer rounded-lg border border-dashed border-border bg-muted/40 p-3.5 text-sm font-semibold text-secondary transition-colors hover:border-brand-studio/40"
        >
          + Create a new campaign or ad group
        </button>

        <button
          type="button"
          (click)="sparkPost()"
          [disabled]="!canSpark()"
          class="primary-button mt-2"
        >
          {{ action() === 'sparking' ? 'Sparking…' : 'Spark this post' }}
        </button>

        <p class="m-0 text-center text-xs text-secondary">
          {{
            isAuthorised()
              ? 'Nothing spends until TikTok approves the ad.'
              : 'Available once the creator authorises the post.'
          }}
        </p>
      </div>
    } @else {
      <!-- No ad groups yet -->
      <div class="flex flex-col gap-y-3.5">
        <div
          class="flex flex-col items-center gap-y-2 rounded-xl border border-dashed border-brand-studio/45 bg-brand-studio/5 p-5 text-center"
        >
          <p class="m-0 text-base font-bold text-primary">Nowhere to put it yet</p>
          <p class="m-0 text-sm text-secondary">
            A Spark ad needs an ad group — that’s the audience and the budget it spends. You’ll
            build one in about two minutes, and this post is carried straight into it.
          </p>
        </div>

        <button type="button" (click)="createAdGroupForThisPost()" class="primary-button">
          Create an ad group for this post
        </button>

        @if (isAuthorised()) {
          <p class="m-0 text-center text-xs text-secondary">
            {{ handle() }}’s authorisation stays valid while you set it up.
          </p>
        }
      </div>
    }
  </section>
</div>
